/**
 * RichTextEditor - Shared Yoopta Editor Component
 *
 * Single source of truth for rich text editing across:
 * - ContentIdeaEditor
 * - ReviewEditor
 * - ArticleEditor
 */

import React, { useRef, useMemo, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Tooltip } from 'antd';
import { AITaskButton } from '../ai';

// Yoopta Editor imports
import YooptaEditor, { createYooptaEditor, YooptaContentValue } from '@yoopta/editor';
import Paragraph from '@yoopta/paragraph';
import { HeadingOne, HeadingTwo, HeadingThree } from '@yoopta/headings';
import { BulletedList, NumberedList, TodoList } from '@yoopta/lists';
import Blockquote from '@yoopta/blockquote';
import Code from '@yoopta/code';
import Link from '@yoopta/link';
import Callout from '@yoopta/callout';
import ImagePlugin from '@yoopta/image';
import Video from '@yoopta/video';
import Embed from '@yoopta/embed';
import FilePlugin from '@yoopta/file';
import ActionMenuList, { DefaultActionMenuRender } from '@yoopta/action-menu-list';
import Toolbar, { DefaultToolbarRender } from '@yoopta/toolbar';
import { Bold, Italic, Underline, Strike, CodeMark, Highlight } from '@yoopta/marks';
import { html } from '@yoopta/exports';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// ============================================================================
// UPLOAD HANDLER
// ============================================================================

const uploadToServer = async (file: File): Promise<{ url: string; sizes?: { width: number; height: number } }> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(`${API_URL}/v1/media/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    withCredentials: true,
  });

  // Worker returns: { key, url, filename, content_type, size, uploaded_at, metadata }
  const uploadedAsset = response.data;

  // For images, try to get dimensions client-side if not provided by server
  let sizes: { width: number; height: number } | undefined;
  if (file.type.startsWith('image/')) {
    sizes = await getImageDimensions(file);
  }

  return {
    url: uploadedAsset.url,
    sizes,
  };
};

// Helper to get image dimensions client-side
const getImageDimensions = (file: File): Promise<{ width: number; height: number } | undefined> => {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      resolve(undefined);
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
};

// ============================================================================
// PLUGIN CONFIGURATION
// ============================================================================

// Configure Image plugin with upload handler
const Image = ImagePlugin.extend({
  options: {
    onUpload: uploadToServer,
    accept: 'image/*',
  },
});

// Configure File plugin with upload handler
const File = FilePlugin.extend({
  options: {
    onUpload: async (file: File) => {
      const result = await uploadToServer(file);
      return {
        src: result.url,
        name: file.name,
        size: file.size,
        format: file.name.split('.').pop() || 'file',
      };
    },
    accept: 'application/pdf,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx',
  },
});

// Yoopta plugins configuration
const plugins = [
  Paragraph,
  HeadingOne,
  HeadingTwo,
  HeadingThree,
  BulletedList,
  NumberedList,
  TodoList,
  Blockquote,
  Code,
  Link,
  Callout,
  Image,
  Video,
  Embed,
  File,
];

const marks = [Bold, Italic, Underline, Strike, CodeMark, Highlight];

const tools = {
  ActionMenu: {
    render: DefaultActionMenuRender,
    tool: ActionMenuList,
  },
  Toolbar: {
    render: DefaultToolbarRender,
    tool: Toolbar,
  },
};

// ============================================================================
// CONTENT CONVERSION UTILITIES
// ============================================================================

/**
 * Convert plain text to Yoopta content format
 * @param text - The text to convert
 * @param markdown - If true, parse markdown headings (# ## ###) and blockquotes (>)
 */
export const textToYooptaContent = (text: string, markdown = false): YooptaContentValue => {
  if (!text) {
    return {
      'block-1': {
        id: 'block-1',
        type: 'Paragraph',
        value: [{ id: 'element-1', type: 'paragraph', children: [{ text: '' }] }],
        meta: { order: 0, depth: 0 },
      },
    };
  }

  const paragraphs = text.split('\n\n').filter(Boolean);
  const content: YooptaContentValue = {};

  paragraphs.forEach((para, index) => {
    const blockId = `block-${index + 1}`;
    const trimmedPara = para.trim();

    // Check for markdown headings and blockquotes if markdown mode is enabled
    if (markdown) {
      if (trimmedPara.startsWith('### ')) {
        content[blockId] = {
          id: blockId,
          type: 'HeadingThree',
          value: [{ id: `element-${index + 1}`, type: 'heading-three', children: [{ text: trimmedPara.slice(4) }] }],
          meta: { order: index, depth: 0 },
        };
        return;
      } else if (trimmedPara.startsWith('## ')) {
        content[blockId] = {
          id: blockId,
          type: 'HeadingTwo',
          value: [{ id: `element-${index + 1}`, type: 'heading-two', children: [{ text: trimmedPara.slice(3) }] }],
          meta: { order: index, depth: 0 },
        };
        return;
      } else if (trimmedPara.startsWith('# ')) {
        content[blockId] = {
          id: blockId,
          type: 'HeadingOne',
          value: [{ id: `element-${index + 1}`, type: 'heading-one', children: [{ text: trimmedPara.slice(2) }] }],
          meta: { order: index, depth: 0 },
        };
        return;
      } else if (trimmedPara.startsWith('> ')) {
        content[blockId] = {
          id: blockId,
          type: 'Blockquote',
          value: [{ id: `element-${index + 1}`, type: 'blockquote', children: [{ text: trimmedPara.slice(2) }] }],
          meta: { order: index, depth: 0 },
        };
        return;
      }
    }

    // Default to paragraph
    content[blockId] = {
      id: blockId,
      type: 'Paragraph',
      value: [{ id: `element-${index + 1}`, type: 'paragraph', children: [{ text: trimmedPara }] }],
      meta: { order: index, depth: 0 },
    };
  });

  return Object.keys(content).length > 0 ? content : {
    'block-1': {
      id: 'block-1',
      type: 'Paragraph',
      value: [{ id: 'element-1', type: 'paragraph', children: [{ text: '' }] }],
      meta: { order: 0, depth: 0 },
    },
  };
};

/**
 * Convert Yoopta content to plain text
 * @param content - The Yoopta content to convert
 * @param markdown - If true, output markdown prefixes for headings and blockquotes
 */
export const yooptaToText = (content: YooptaContentValue, markdown = false): string => {
  if (!content) return '';

  const blocks = Object.values(content).sort((a, b) => a.meta.order - b.meta.order);
  const textParts: string[] = [];

  blocks.forEach((block) => {
    if (block.value && Array.isArray(block.value)) {
      block.value.forEach((element: any) => {
        if (element.children) {
          const text = element.children.map((child: any) => child.text || '').join('');
          if (text) {
            if (markdown) {
              // Add markdown prefix based on block type
              if (block.type === 'HeadingOne') {
                textParts.push(`# ${text}`);
              } else if (block.type === 'HeadingTwo') {
                textParts.push(`## ${text}`);
              } else if (block.type === 'HeadingThree') {
                textParts.push(`### ${text}`);
              } else if (block.type === 'Blockquote') {
                textParts.push(`> ${text}`);
              } else {
                textParts.push(text);
              }
            } else {
              textParts.push(text);
            }
          }
        }
      });
    }
  });

  return textParts.join('\n\n');
};

/**
 * Convert Yoopta content to HTML
 */
export const yooptaToHtml = (editor: ReturnType<typeof createYooptaEditor>, content: YooptaContentValue): string => {
  if (!content || Object.keys(content).length === 0) return '';
  return html.serialize(editor, content);
};

/**
 * Count words in Yoopta content
 */
export const countWords = (content: YooptaContentValue): number => {
  const text = yooptaToText(content);
  return text.trim() ? text.trim().split(/\s+/).length : 0;
};

// ============================================================================
// RICH TEXT EDITOR COMPONENT
// ============================================================================

export interface RichTextEditorProps {
  /** Initial content value */
  value: YooptaContentValue;
  /** Called when content changes */
  onChange: (value: YooptaContentValue) => void;
  /** Called once when the editor instance is ready — use for html.serialize/deserialize */
  onEditorReady?: (editor: ReturnType<typeof createYooptaEditor>) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Minimum height in pixels */
  minHeight?: number;
  /** Whether the editor is in fullscreen mode */
  fullscreen?: boolean;
  /** Whether to show resize handle */
  resizable?: boolean;
  /** Custom className for the container */
  className?: string;
  /** Whether the editor should auto-focus */
  autoFocus?: boolean;
  /** Whether the editor is read-only */
  readOnly?: boolean;
  /** Whether to show AI task button */
  showAIButton?: boolean;
  /** Whether to fill the parent container height */
  fillParent?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  onEditorReady,
  placeholder = 'Start writing... (Type "/" for commands)',
  minHeight = 200,
  fullscreen = false,
  resizable = true,
  className = '',
  autoFocus = false,
  readOnly = false,
  showAIButton = false,
  fillParent = false,
}) => {
  const editor = useMemo(() => createYooptaEditor(), []);
  const selectionRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(minHeight);
  const [isResizing, setIsResizing] = useState(false);

  // Notify parent when editor is ready (once on mount)
  const onEditorReadyRef = useRef(onEditorReady);
  useEffect(() => {
    onEditorReadyRef.current?.(editor);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);

    const startY = e.clientY;
    const startHeight = height;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const newHeight = Math.max(minHeight, startHeight + deltaY);
      setHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [height, minHeight]);

  // Auto-focus when requested
  useEffect(() => {
    if (autoFocus && editor) {
      // Focus the editor after a short delay to ensure it's mounted
      const timer = setTimeout(() => {
        editor.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus, editor]);

  const containerHeight = fullscreen ? 'calc(100vh - 400px)' : (fillParent ? '100%' : height);

  // Handle AI content changes - convert plain text back to Yoopta format
  const handleAIContentChange = useCallback((newContent: string) => {
    const yooptaContent = textToYooptaContent(newContent, true);
    onChange(yooptaContent);
  }, [onChange]);

  // Get current content as plain text for AI processing
  const currentTextContent = useMemo(() => yooptaToText(value, true), [value]);

  return (
    <div
      ref={containerRef}
      className={`rich-text-editor-container ${className}`}
      style={{
        position: 'relative',
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        background: '#fff',
        // Removed overflow: hidden to allow slash menu to overflow
        height: fillParent ? '100%' : 'auto',
      }}
    >
      {/* Editor Area */}
      <div
        ref={selectionRef}
        className="rich-text-editor-content"
        style={{
          height: containerHeight,
          minHeight: minHeight,
          overflow: 'auto',
          padding: fullscreen ? '24px 10%' : '12px 16px',
          position: 'relative',
        }}
      >
        <YooptaEditor
          editor={editor}
          plugins={plugins}
          marks={marks}
          tools={readOnly ? {} : tools}
          value={value}
          onChange={onChange}
          selectionBoxRoot={selectionRef}
          placeholder={placeholder}
          readOnly={readOnly}
          style={{
            width: '100%',
            minHeight: '100%',
            paddingBottom: fillParent ? 40 : 0, // Extra space at bottom
          }}
        />
      </div>

      {/* AI Task Button */}
      {showAIButton && !readOnly && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
          }}
        >
          <Tooltip title="AI Tasks">
            <AITaskButton
              content={currentTextContent}
              onContentChange={handleAIContentChange}
              size="small"
              type="text"
            />
          </Tooltip>
        </div>
      )}

      {/* Resize Handle (only if not fillParent) */}
      {resizable && !fullscreen && !fillParent && (
        <div
          onMouseDown={handleMouseDown}
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: 20,
            height: 20,
            cursor: 'nwse-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isResizing ? 1 : 0.5,
            transition: 'opacity 0.2s',
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            if (!isResizing) {
              e.currentTarget.style.opacity = '0.5';
            }
          }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            style={{ transform: 'rotate(90deg)' }}
          >
            <path
              d="M1 9L9 1M5 9L9 5M9 9L9 9"
              stroke="#999"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}

      {/* Custom styles for Yoopta - Notion-like experience */}
      <style>{`
        .rich-text-editor-content {
          scroll-behavior: smooth;
        }

        .rich-text-editor-container .yoopta-editor {
          font-size: 15px;
          line-height: 1.5; /* Reduced from 1.7 */
          color: #37352f;
        }

        /* Prevent scroll jump on Enter */
        .rich-text-editor-container [contenteditable="true"] {
          outline: none;
        }

        /* Block hover effects - Notion style */
        .rich-text-editor-container [data-yoopta-element-type] {
          position: relative;
          padding: 1px 0; /* Reduced from 3px 0 */
          border-radius: 4px;
          transition: background 0.1s ease;
        }

        .rich-text-editor-container [data-yoopta-element-type]:hover {
          background: rgba(55, 53, 47, 0.03);
        }

        /* Block actions - side buttons */
        .rich-text-editor-container .yoopta-block-actions {
          position: absolute;
          left: -52px; /* Adjusted for larger buttons */
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          gap: 6px; /* Increased gap */
          opacity: 0;
          transition: opacity 0.15s;
        }

        .rich-text-editor-container [data-yoopta-element-type]:hover .yoopta-block-actions,
        .rich-text-editor-container .yoopta-block-actions:hover {
          opacity: 1;
        }

        .rich-text-editor-container .yoopta-block-actions button {
          width: 24px; /* Increased from 20px */
          height: 24px; /* Increased from 20px */
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          border-radius: 4px;
          cursor: pointer;
          color: #9b9a97;
        }

        .rich-text-editor-container .yoopta-block-actions button:hover {
          background: rgba(55, 53, 47, 0.08);
          color: #37352f;
        }

        /* Drag handle cursor */
        .rich-text-editor-container .yoopta-block-actions button[draggable="true"] {
          cursor: grab;
        }

        .rich-text-editor-container .yoopta-block-actions button[draggable="true"]:active {
          cursor: grabbing;
        }

        /* Action menu (slash command) - Notion style */
        .yoopta-action-menu-list {
          max-height: 350px;
          overflow-y: auto;
          background: #fff;
          border: none;
          border-radius: 6px;
          box-shadow: rgba(15, 15, 15, 0.05) 0px 0px 0px 1px, rgba(15, 15, 15, 0.1) 0px 3px 6px, rgba(15, 15, 15, 0.2) 0px 9px 24px;
          padding: 6px;
          min-width: 280px;
          z-index: 10000; /* High Z-Index */
        }


        /* Search input in action menu */
        .yoopta-action-menu-custom .action-menu-search {
          padding: 8px 12px;
          border-bottom: 1px solid rgba(55, 53, 47, 0.09);
          margin-bottom: 6px;
        }

        .yoopta-action-menu-custom .action-menu-search input {
          width: 100%;
          border: none;
          outline: none;
          font-size: 14px;
          background: transparent;
          color: #37352f;
        }

        .yoopta-action-menu-custom .action-menu-search input::placeholder {
          color: #9b9a97;
        }

        /* Action menu items */
        .yoopta-action-menu-list-item {
          display: flex;
          align-items: center;
          padding: 6px 10px;
          border-radius: 4px;
          cursor: pointer;
          gap: 10px;
          min-height: 32px;
        }

        .yoopta-action-menu-list-item:hover,
        .yoopta-action-menu-list-item[data-active="true"] {
          background: rgba(55, 53, 47, 0.08);
        }

        .yoopta-action-menu-list-item-icon {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #37352f;
          background: rgba(55, 53, 47, 0.06);
          border-radius: 4px;
          font-size: 14px;
        }

        .yoopta-action-menu-list-item-title {
          font-size: 14px;
          font-weight: 500;
          color: #37352f;
        }

        .yoopta-action-menu-list-item-description {
          font-size: 12px;
          color: #9b9a97;
          margin-left: auto;
        }

        /* Section headers in action menu */
        .yoopta-action-menu-list-group {
          padding: 6px 10px 4px;
          font-size: 11px;
          font-weight: 500;
          color: #9b9a97;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Toolbar - floating bubble style */
        .yoopta-toolbar {
          background: #fff;
          border: none;
          border-radius: 6px;
          box-shadow: rgba(15, 15, 15, 0.05) 0px 0px 0px 1px, rgba(15, 15, 15, 0.1) 0px 3px 6px, rgba(15, 15, 15, 0.2) 0px 9px 24px;
          padding: 4px;
          display: flex;
          gap: 1px;
        }

        .yoopta-toolbar button {
          width: 28px;
          height: 28px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          border-radius: 4px;
          cursor: pointer;
          color: #37352f;
        }

        .yoopta-toolbar button:hover {
          background: rgba(55, 53, 47, 0.08);
        }

        .yoopta-toolbar button[data-active="true"] {
          background: rgba(35, 131, 226, 0.14);
          color: #2383e2;
        }

        /* Divider in toolbar */
        .yoopta-toolbar-divider {
          width: 1px;
          background: rgba(55, 53, 47, 0.09);
          margin: 4px 4px;
        }

        /* Placeholder styling - Notion style */
        .rich-text-editor-container .yoopta-editor [data-placeholder]::before {
          color: rgba(55, 53, 47, 0.4);
          font-style: normal;
        }

        /* Heading styles */
        .rich-text-editor-container h1[data-yoopta-element-type] {
          font-size: 1.875em;
          font-weight: 700;
          line-height: 1.3;
          margin-top: 1em;
        }

        .rich-text-editor-container h2[data-yoopta-element-type] {
          font-size: 1.5em;
          font-weight: 600;
          line-height: 1.3;
          margin-top: 0.75em;
        }

        .rich-text-editor-container h3[data-yoopta-element-type] {
          font-size: 1.25em;
          font-weight: 600;
          line-height: 1.3;
          margin-top: 0.5em;
        }

        /* Quote style */
        .rich-text-editor-container blockquote[data-yoopta-element-type] {
          padding-left: 16px;
          border-left: 3px solid #37352f;
          color: #37352f;
        }

        /* Code block */
        .rich-text-editor-container pre[data-yoopta-element-type] {
          background: #f7f6f3;
          border-radius: 4px;
          padding: 12px 16px;
          font-family: 'SFMono-Regular', Menlo, Consolas, 'PT Mono', 'Liberation Mono', Courier, monospace;
          font-size: 0.875em;
          overflow-x: auto;
        }

        /* Callout */
        .rich-text-editor-container [data-yoopta-element-type="callout"] {
          background: rgba(241, 241, 239, 0.5);
          border-radius: 4px;
          padding: 16px;
          display: flex;
          gap: 8px;
        }

        /* Todo list */
        .rich-text-editor-container [data-yoopta-element-type="todo-list"] {
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }

        .rich-text-editor-container [data-yoopta-element-type="todo-list"] input[type="checkbox"] {
          width: 16px;
          height: 16px;
          margin-top: 4px;
          cursor: pointer;
        }

        /* Image styling with resize support */
        .rich-text-editor-container [data-yoopta-element-type="image"] {
          position: relative;
          display: inline-block;
          max-width: 100%;
        }

        .rich-text-editor-container [data-yoopta-element-type="image"] img {
          border-radius: 4px;
          max-width: 100%;
          display: block;
          cursor: pointer;
        }

        /* Image container with resize handles on selection */
        .rich-text-editor-container [data-yoopta-element-type="image"]:focus-within,
        .rich-text-editor-container [data-yoopta-element-type="image"]:hover {
          outline: 2px solid #1890ff;
          outline-offset: 2px;
        }

        /* Resizable image wrapper */
        .rich-text-editor-container [data-yoopta-element-type="image"] > div {
          resize: both;
          overflow: hidden;
          min-width: 100px;
          min-height: 50px;
          max-width: 100%;
        }

        .rich-text-editor-container [data-yoopta-element-type="image"] > div > img {
          width: 100%;
          height: auto;
          object-fit: contain;
        }

        /* Image size presets - can be applied via className */
        .rich-text-editor-container [data-yoopta-element-type="image"].size-small > div {
          max-width: 300px;
        }
        .rich-text-editor-container [data-yoopta-element-type="image"].size-medium > div {
          max-width: 500px;
        }
        .rich-text-editor-container [data-yoopta-element-type="image"].size-large > div {
          max-width: 800px;
        }
        .rich-text-editor-container [data-yoopta-element-type="image"].size-full > div {
          max-width: 100%;
        }

        /* Image alignment */
        .rich-text-editor-container [data-yoopta-element-type="image"].align-left {
          display: block;
          margin-right: auto;
        }
        .rich-text-editor-container [data-yoopta-element-type="image"].align-center {
          display: block;
          margin-left: auto;
          margin-right: auto;
        }
        .rich-text-editor-container [data-yoopta-element-type="image"].align-right {
          display: block;
          margin-left: auto;
        }

        /* Links */
        .rich-text-editor-container a {
          color: #37352f;
          text-decoration: underline;
          text-decoration-color: rgba(55, 53, 47, 0.4);
          text-underline-offset: 2px;
        }

        .rich-text-editor-container a:hover {
          text-decoration-color: #37352f;
        }

        /* Selection highlight */
        .rich-text-editor-container ::selection {
          background: rgba(35, 131, 226, 0.28);
        }

        /* Focus outline for accessibility */
        .rich-text-editor-container:focus-within {
          box-shadow: 0 0 0 2px rgba(35, 131, 226, 0.35);
        }
      `}</style>
    </div>
  );
};

// Export the editor factory and serializers for advanced usage
export { createYooptaEditor, html };
export type { YooptaContentValue };

/**
 * Detect if a string is HTML content
 */
export const isHtmlContent = (content: string): boolean => {
  if (!content?.trim()) return false;
  return /<[a-z][\s\S]*>/i.test(content.trim());
};

/**
 * Convert any stored description (HTML, markdown, or plain text) to Yoopta content.
 * Requires the editor instance for HTML deserialization.
 */
export const descriptionToYooptaContent = (
  description: string | null | undefined,
  editor: ReturnType<typeof createYooptaEditor> | null
): YooptaContentValue => {
  const text = description || '';
  if (!text.trim()) return textToYooptaContent('');

  if (isHtmlContent(text) && editor) {
    try {
      const result = html.deserialize(editor, text);
      if (result && Object.keys(result).length > 0) return result;
    } catch {
      // Fall through to plain text
    }
  }

  return textToYooptaContent(text, true);
};

/**
 * Serialize Yoopta content to HTML for storage.
 * Falls back to plain text if editor not available.
 */
export const yooptaContentToHtml = (
  content: YooptaContentValue,
  editor: ReturnType<typeof createYooptaEditor> | null
): string => {
  if (!editor) return yooptaToText(content, true);
  try {
    return html.serialize(editor, content);
  } catch {
    return yooptaToText(content, true);
  }
};
